import React, { useCallback, useContext, useEffect, useRef } from 'react';
import { SwitchThemeContext, NotificationDispatch } from '@tryhackme/thm-ui-components';
import tinymce, { RawEditorOptions } from 'tinymce';
import { ThemeColorMode } from 'src/common/enums';
import { useEditorSetup } from '../hooks/use-editor-setup';
import { getInlineEditorConfig, loadEditorTheme } from './tinymce-config';
import { SimulatedInput } from './inline-editor.styles';
import './plugins/thm-markdown.plugin';
import './plugins/enhanced-code-editor.plugin';
import { RichTextEditorProps } from '.';
export const InlineRichTextEditor: React.FC<Pick<RichTextEditorProps, 'content' | 'onChange'>> = ({
  content,
  onChange
}) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const {
    theme
  } = useContext(SwitchThemeContext);
  const isDarkMode = theme === ThemeColorMode.DARK;
  const {
    editorInstanceRef,
    isInitializedRef,
    handleEditorInit
  } = useEditorSetup(content, onChange);
  const initializeEditor = useCallback(async () => {
    try {
      if (editorInstanceRef.current) {
        editorInstanceRef.current.destroy();
        editorInstanceRef.current = null;
      }
      isInitializedRef.current = false;
      await loadEditorTheme(isDarkMode);
      if (!window.tinymce) return;
      const inlineEditorConfig = getInlineEditorConfig(isDarkMode);
      const config = {
        target: editorRef.current || undefined,
        ...inlineEditorConfig,
        setup: handleEditorInit,
        toolbar_mode: ('sliding' as RawEditorOptions['toolbar_mode'])
      };
      await tinymce.init(config);
    } catch {
      NotificationDispatch('error', 'Error initializing editor');
    }
  }, [editorInstanceRef, isInitializedRef, isDarkMode, handleEditorInit]);
  useEffect(() => {
    const init = () => {
      initializeEditor().catch(() => {
        NotificationDispatch('error', 'Error initializing editor');
      });
    };
    init();
    return () => {
      if (editorInstanceRef.current) {
        editorInstanceRef.current.destroy();
        editorInstanceRef.current = null;
      }
      isInitializedRef.current = false;
    };
  }, [editorInstanceRef, initializeEditor, isInitializedRef]);
  return <SimulatedInput key={isDarkMode ? 'dark' : 'light'} isDarkMode={isDarkMode} contentEditable ref={editorRef} data-testid="inline-editor" data-sentry-element="SimulatedInput" data-sentry-component="InlineRichTextEditor" data-sentry-source-file="inline-editor.tsx" />;
};